/**
 * Advanced targeting on the Create ad wizard's audience step: interests, keywords and follower
 * look-alikes (PR 4). The decisions are in `lib/primary-targeting.ts`, the words in
 * `lib/primary-targeting-copy.ts`; this file renders them and holds one search.
 *
 * 🔴 The block opens with `WIDENS`, because everything in it adds people: X unions these
 * types and ANDs the rest (campaign-management.md:586–588). The countries above narrow; this
 * block, unlike every field above it, widens.
 *
 * Nothing is sent from here. The chosen values land in the draft, and the wizard's run sends
 * one criterion each, after the carriers (`criteriaToSend`).
 */
import { useEffect, useState } from "react";
import { type TargetingInterest, UnauthorizedError, api } from "../lib/api.ts";
import { SEARCH_DEBOUNCE_MS } from "../lib/create-ad.ts";
import {
	ADD_INTEREST,
	ADD_KEYWORD,
	ADD_LOOKALIKE,
	ADVANCED_HEADING,
	INTERESTS_CHOSEN,
	INTERESTS_NOT_READ,
	INTEREST_PLACEHOLDER,
	INTEREST_SEARCH_LABEL,
	KEYWORDS_CHOSEN,
	KEYWORD_HELP,
	KEYWORD_LABEL,
	KEYWORD_TYPE_LABEL,
	KEYWORD_TYPE_NAMES,
	LOOKALIKES_CHOSEN,
	LOOKALIKE_HELP,
	LOOKALIKE_LABEL,
	NO_INTEREST_MATCH,
	PLACEMENT_NOTE,
	PRIMARY_PROBLEMS,
	WIDENS,
	keywordLabel,
	limitsNote,
} from "../lib/primary-targeting-copy.ts";
import {
	KEYWORD_TYPES,
	type KeywordType,
	PRIMARY_LIMITS,
	type PrimaryProblem,
	keywordProblem,
	lookalikeProblem,
} from "../lib/primary-targeting.ts";
import { Button, InfoBanner, Input, Select } from "../ui/index.ts";

export interface PrimaryChoice {
	interests: TargetingInterest[];
	keywords: { type: KeywordType; text: string }[];
	lookalikes: string[];
}

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function PrimaryTargeting({
	value,
	onChange,
}: {
	value: PrimaryChoice;
	onChange: (next: Partial<PrimaryChoice>) => void;
}) {
	const [query, setQuery] = useState("");
	const [found, setFound] = useState<TargetingInterest[] | null>(null);
	const [failed, setFailed] = useState<string | null>(null);
	useEffect(() => {
		const q = query.trim();
		if (q === "") {
			setFound(null);
			setFailed(null);
			return;
		}
		// As the country search: an answer for a query no longer typed is dropped.
		let stale = false;
		const timer = setTimeout(() => {
			api
				.targetingInterests(q)
				.then((page) => {
					if (stale) return;
					setFound(page.interests);
					setFailed(null);
				})
				.catch((e: unknown) => {
					if (stale || e instanceof UnauthorizedError) return;
					setFound(null);
					setFailed(message(e));
				});
		}, SEARCH_DEBOUNCE_MS);
		return () => {
			stale = true;
			clearTimeout(timer);
		};
	}, [query]);

	const [keyword, setKeyword] = useState("");
	const [keywordType, setKeywordType] = useState<KeywordType>("BROAD_KEYWORD");
	const [keywordShown, setKeywordShown] = useState<PrimaryProblem | null>(null);
	const [lookalike, setLookalike] = useState("");
	const [lookalikeShown, setLookalikeShown] = useState<PrimaryProblem | null>(null);

	const addKeyword = () => {
		const problem = keywordProblem(keyword, keywordType, value.keywords);
		setKeywordShown(problem);
		if (problem !== null) return;
		onChange({ keywords: [...value.keywords, { type: keywordType, text: keyword }] });
		setKeyword("");
	};
	const addLookalike = () => {
		const problem = lookalikeProblem(lookalike);
		setLookalikeShown(problem);
		if (problem !== null || value.lookalikes.includes(lookalike)) return;
		onChange({ lookalikes: [...value.lookalikes, lookalike] });
		setLookalike("");
	};

	return (
		<div className="grid gap-3 border-t border-border pt-4">
			<p className="text-sm text-content">{ADVANCED_HEADING}</p>
			<p className="text-sm text-muted">{WIDENS}</p>
			<p className="text-xs text-muted">{PLACEMENT_NOTE}</p>
			<p className="text-xs text-muted">
				{limitsNote(PRIMARY_LIMITS.interests, PRIMARY_LIMITS.keywords, PRIMARY_LIMITS.lookalikes)}
			</p>

			<Input
				label={INTEREST_SEARCH_LABEL}
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				placeholder={INTEREST_PLACEHOLDER}
			/>
			{failed && (
				<InfoBanner variant="danger" title={INTERESTS_NOT_READ}>
					{failed}
				</InfoBanner>
			)}
			{found !== null && (
				<ul className="grid gap-2">
					{found.length === 0 && <li className="text-sm text-muted">{NO_INTEREST_MATCH}</li>}
					{found.map((i) => (
						<li key={i.targetingValue} className="flex items-center gap-3">
							<span className="text-sm text-content">{i.name}</span>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								disabled={value.interests.some((k) => k.targetingValue === i.targetingValue)}
								onClick={() => onChange({ interests: [...value.interests, i] })}
							>
								{ADD_INTEREST}
							</Button>
						</li>
					))}
				</ul>
			)}
			<Chips
				label={INTERESTS_CHOSEN}
				rows={value.interests.map((i) => ({ key: i.targetingValue, name: i.name }))}
				remove={(key) =>
					onChange({ interests: value.interests.filter((i) => i.targetingValue !== key) })
				}
			/>

			<div className="grid gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-end">
				<Input
					label={KEYWORD_LABEL}
					value={keyword}
					onChange={(e) => setKeyword(e.target.value)}
					error={keywordShown ? PRIMARY_PROBLEMS[keywordShown] : undefined}
				/>
				<Select
					label={KEYWORD_TYPE_LABEL}
					value={keywordType}
					onChange={(e) => setKeywordType(e.target.value as KeywordType)}
					options={KEYWORD_TYPES.map((t) => ({ value: t, label: KEYWORD_TYPE_NAMES[t] }))}
				/>
				<Button type="button" variant="ghost" onClick={addKeyword}>
					{ADD_KEYWORD}
				</Button>
			</div>
			<p className="text-xs text-muted">{KEYWORD_HELP}</p>
			<Chips
				label={KEYWORDS_CHOSEN}
				rows={value.keywords.map((k) => ({ key: `${k.type}:${k.text}`, name: keywordLabel(k) }))}
				remove={(key) =>
					onChange({ keywords: value.keywords.filter((k) => `${k.type}:${k.text}` !== key) })
				}
			/>

			<div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
				<Input
					label={LOOKALIKE_LABEL}
					inputMode="numeric"
					value={lookalike}
					onChange={(e) => setLookalike(e.target.value)}
					error={lookalikeShown ? PRIMARY_PROBLEMS[lookalikeShown] : undefined}
				/>
				<Button type="button" variant="ghost" onClick={addLookalike}>
					{ADD_LOOKALIKE}
				</Button>
			</div>
			<p className="text-xs text-muted">{LOOKALIKE_HELP}</p>
			<Chips
				label={LOOKALIKES_CHOSEN}
				rows={value.lookalikes.map((id) => ({ key: id, name: id }))}
				remove={(key) => onChange({ lookalikes: value.lookalikes.filter((id) => id !== key) })}
			/>
		</div>
	);
}

/** What is chosen, each with its ×. Nothing when empty. */
function Chips({
	label,
	rows,
	remove,
}: {
	label: string;
	rows: { key: string; name: string }[];
	remove: (key: string) => void;
}) {
	if (rows.length === 0) return null;
	return (
		<div className="flex flex-wrap items-center gap-2">
			<span className="text-sm text-muted">{label}</span>
			{rows.map((r) => (
				<span
					key={r.key}
					className="inline-flex items-center gap-1 rounded-md bg-surface-2 px-2 py-1 text-xs text-content"
				>
					{r.name}
					<button
						type="button"
						className="text-muted hover:text-content"
						aria-label={`Remove ${r.name}`}
						onClick={() => remove(r.key)}
					>
						×
					</button>
				</span>
			))}
		</div>
	);
}
